import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated'
import type { Time } from '@/lib/time'
import type { TimeEntryFormValues } from '@/modules/timesheets/types'
import React from 'react'
import { useTimeEntryForm } from '../hooks'
import type {
  TimesheetsDayReportTimeEntry,
  TimesheetsSalaryWorkTime,
} from '../types.graphql'
import { TimeEntryForm } from './TimeEntryForm'

export interface EditTimeEntryFormData {
  dateStart: Date
  timeStart: Time
  dateEnd: Date
  timeEnd: Time
}

interface EditTimeEntryModalProps {
  entry: TimesheetsDayReportTimeEntry | null
  salaryWorkTime: TimesheetsSalaryWorkTime
  overtimeCustomChoice: boolean
  existingTimeEntries: TimesheetsDayReportTimeEntry[]
  canSetSalaryCodeManually: boolean
  onResolve: PromptResolve<TimeEntryFormValues | null>
}

export const EditTimeEntryModal: React.FC<EditTimeEntryModalProps> = ({
  entry,
  salaryWorkTime,
  overtimeCustomChoice,
  existingTimeEntries,
  canSetSalaryCodeManually,
  onResolve,
}) => {
  const translations = useTranslate({
    editTimeEntry: 'timesheets.edit-time-entry',
    from: 'common.from',
    until: 'common.until',

    cancel: 'common.cancel',
    submit: 'common.submit',
    comment: 'common.comment',

    error: {
      invalid: 'error.invalid-value',
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
    },
  })

  const form = useTimeEntryForm(entry, salaryWorkTime)

  const customOvertimeForm = {
    ...form,
    dayOvertimeLimit: salaryWorkTime.dailyWorkLimit,
  }

  const overtimeOptionActive =
    overtimeCustomChoice &&
    (form.formValues.end.getTime() - form.formValues.start.getTime()) / 1000 >
      salaryWorkTime.dailyWorkLimit

  const formValid =
    form.formValid &&
    ((overtimeOptionActive && !!form.formValues.overtimeSalaryCodeId) ||
      !overtimeOptionActive ||
      salaryWorkTime.usePeriodOvertime)

  return (
    <RegularModal
      title={translations.editTimeEntry}
      submitText={translations.submit}
      width="600px"
      onCancel={() => onResolve(null)}
      onSubmit={() => onResolve(form.formValues)}
      submitDisabled={!formValid}
      cancelOnClickOutside
      overflowY="auto"
    >
      <TimeEntryForm
        onSubmit={onResolve}
        form={customOvertimeForm}
        overtimeCustomChoice={overtimeCustomChoice}
        salaryWorkTime={salaryWorkTime}
        existingTimeEntries={existingTimeEntries}
        canSetSalaryCodeManually={canSetSalaryCodeManually}
      />
    </RegularModal>
  )
}
